import React, { useState, useCallback } from "react";
import { ShieldCheck } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import LanguageToggle from "./LanguageToggle";
import { useLocaleContext } from "../context/LocaleContext";
import { getTranslation } from "../utils/translations";
import { useAuth } from "./AuthProvider";
import { useSubscription } from "../hooks/useSubscription";
import ExitIntentModal from "./ExitIntentModal";
import { trackRybbitPaywallExitButtonClicked } from "../utils/rybbit";
import MobileNav from "./navigation/MobileNav";
import UserMenu from "./navigation/UserMenu";
import ToolsDropdown from "./navigation/ToolsDropdown";
import ResourcesDropdown from "./navigation/ResourcesDropdown";
import ApiDropdown from "./navigation/ApiDropdown";

export const Header: React.FC = React.memo(() => {
  const { locale } = useLocaleContext();
  const { user, signOut, signIn } = useAuth();
  const { hasActiveSubscription } = useSubscription();
  const [showExitModal, setShowExitModal] = useState(false);

  const handleLogoutClick = useCallback(async () => {
    // If user is signed in but NOT premium, show exit intent modal
    if (user && !hasActiveSubscription()) {
      // Track that user clicked logout button on paywall (exit intent trigger)
      if (typeof window !== "undefined" && window.datafast) {
        window.datafast("goal", "paywall_exit_button_clicked", {});
      }
      trackRybbitPaywallExitButtonClicked({
        user_tier: "free",
        time_on_paywall_seconds: 0,
      });
      setShowExitModal(true);
    } else {
      // Premium users or anonymous users can logout directly
      try {
        await signOut();
      } catch (error) {
        console.error("Sign out failed, forcing page reload:", error);
        window.location.href = "/";
      }
    }
  }, [user, hasActiveSubscription, signOut]);

  const handleActualLogout = useCallback(async () => {
    try {
      await signOut();
    } catch (error) {
      console.error("Sign out failed, forcing page reload:", error);
      window.location.href = "/";
    }
  }, [signOut]);

  const handleLogoClick = useCallback(() => {
    const homeRoutes: Record<string, string> = {
      en: "/",
      de: "/de",
      "pt-BR": "/pt-br",
      "ms-MY": "/my",
      es: "/es",
      fr: "/fr",
      ja: "/ja",
      id: "/id",
    };
    const targetRoute = homeRoutes[locale] || "/";

    // Force full page reload to avoid React Router state issues
    window.location.href = targetRoute;
  }, [locale]);

  return (
    <header className="relative bg-white dark:bg-neutral-950 border-b border-neutral-200 dark:border-neutral-800 min-h-[120px] lg:min-h-[72px]">
      <div className="container mx-auto px-4 md:px-6 max-w-[1400px] w-full py-4">
        {/* Mobile Layout: 2-row stacked */}
        <div className="lg:hidden">
          {/* Row 1: Logo + Menu */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-2 hover:text-accent-600 dark:hover:text-accent-400 transition-colors cursor-pointer bg-transparent border-0 p-0 flex-1 min-w-0"
              aria-label="Go to homepage"
            >
              <ShieldCheck
                className="w-7 h-7 text-accent-600 dark:text-accent-400 flex-shrink-0"
                aria-hidden="true"
              />
              <div className="text-lg font-bold text-neutral-900 dark:text-white truncate">
                {getTranslation(locale, "header.title")}
              </div>
            </button>

            {/* Mobile Menu Controls */}
            <MobileNav user={user} onSignIn={signIn} />
          </div>

          {/* Row 2: User Avatar (if signed in) + Language + Theme */}
          <div className="flex items-center justify-between gap-3 min-h-[40px]">
            <div className="flex items-center gap-2">
              {user && (
                <UserMenu
                  userEmail={user.email}
                  hasActiveSubscription={hasActiveSubscription()}
                  onLogoutClick={handleLogoutClick}
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              <LanguageToggle />
              <ThemeToggle />
            </div>
          </div>
        </div>

        {/* Desktop Layout: Single row with navigation */}
        <div className="hidden lg:flex items-center justify-between gap-4">
          {/* Left Side: Logo + Navigation */}
          <div className="flex items-center gap-8">
            {/* Logo */}
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-2 hover:text-accent-600 dark:hover:text-accent-400 transition-colors cursor-pointer bg-transparent border-0 p-0 flex-shrink-0"
              aria-label="Go to homepage"
            >
              <ShieldCheck
                className="w-8 h-8 text-accent-600 dark:text-accent-400 flex-shrink-0"
                aria-hidden="true"
              />
              <div className="text-2xl font-bold text-neutral-900 dark:text-white whitespace-nowrap">
                {getTranslation(locale, "header.title")}
              </div>
            </button>

            {/* Primary Navigation Dropdowns */}
            <nav className="flex items-center gap-2" role="navigation" aria-label="Primary navigation">
              <ToolsDropdown />
              <ResourcesDropdown />
              <ApiDropdown />
            </nav>
          </div>

          {/* Right Side: Auth + Language & Theme Toggles */}
          <div className="flex items-center gap-4 flex-shrink-0">
            {/* Auth Section */}
            {!user ? (
              <button
                onClick={signIn}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-900 dark:text-white bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 shadow-sm hover:shadow transition-all"
              >
                <img
                  src="/google-icon.svg"
                  alt="Google"
                  width="16"
                  height="16"
                  loading="eager"
                  decoding="async"
                  className="w-4 h-4 flex-shrink-0"
                />
                <span className="whitespace-nowrap">
                  {locale === 'de'
                    ? 'Anmelden'
                    : locale === 'fr'
                    ? 'Connexion'
                    : locale === 'ja'
                    ? 'ログイン'
                    : locale === 'id'
                    ? 'Masuk'
                    : 'Sign In'}
                </span>
              </button>
            ) : (
              <UserMenu
                userEmail={user.email}
                hasActiveSubscription={hasActiveSubscription()}
                onLogoutClick={handleLogoutClick}
              />
            )}

            {/* Language & Theme Toggles */}
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Exit Intent Modal */}
      <ExitIntentModal
        isOpen={showExitModal}
        onClose={() => setShowExitModal(false)}
        onLogout={handleActualLogout}
      />
    </header>
  );
});

Header.displayName = "Header";
